import React from "react";
import { Printer } from "lucide-react";

/*
 * Turning a results page into a printed report.
 *
 * The report is the page itself, restyled by the print stylesheet, rather than
 * a second rendering — a separate report template drifts out of step with what
 * everyone just read on screen, and then two things have to be kept correct.
 *
 * The browser's own print pipeline gives a real PDF on every platform ("Save as
 * PDF" in the print dialog), so there is no PDF library to carry, nothing to
 * load from a CDN, and the output honours the reader's paper size.
 */

/** The button. Hidden on paper, because a button on paper is nonsense. */
export function PrintButton({ label = "Print / PDF" }: { label?: string }) {
  return (
    <button
      onClick={() => window.print()}
      className="no-print inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-[13px] font-semibold text-ink transition-colors hover:bg-slate-50"
    >
      <Printer size={15} /> {label}
    </button>
  );
}

/*
 * The masthead every printed report carries, so a sheet that has left the
 * building still says what it is: who ran the meeting, which event, and when it
 * was printed — a results sheet with no date on it is worse than none, because
 * nobody can tell whether it is the current one.
 */
export function PrintHeader({
  title,
  subtitle,
  meta = [],
}: {
  title: string;
  subtitle?: string;
  meta?: (string | undefined | null)[];
}) {
  const printedAt = new Date().toLocaleString("en-AU", { dateStyle: "long", timeStyle: "short" });
  return (
    <div className="print-only mb-4 border-b-2 border-black pb-2">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="text-[16pt] font-extrabold leading-tight">{title}</h1>
        <span className="text-[9pt]">GRS Shoot Results Portal</span>
      </div>
      {subtitle && <div className="mt-0.5 text-[11pt] font-semibold">{subtitle}</div>}
      <div className="mt-1 flex flex-wrap gap-x-4 text-[9pt]">
        {meta.filter(Boolean).map((m) => (
          <span key={String(m)}>{m}</span>
        ))}
        <span className="ml-auto">Printed {printedAt}</span>
      </div>
    </div>
  );
}

/*
 * The footnote. Results are re-published whenever a shoot is corrected, so a
 * printed sheet has to say that it is a snapshot rather than the record.
 */
export function PrintFooter() {
  return (
    <p className="print-only mt-4 border-t border-black pt-1.5 text-[8.5pt]">
      Scores are shown in fullbore notation — 48.5V is 48 points including five V-bulls. Placings are within each class
      and grade. This sheet is a snapshot; the portal carries the current results.
    </p>
  );
}
